import type { ReactNode } from "react";
import type {
  ColumnDef,
  ColumnFiltersState,
  Table,
} from "@tanstack/react-table";
import type { DataTableProps } from "../../molecules/DataTable/DataTable.types";

/** TanStack column sizing for the organism-injected selection column. */
export const organismSelectionColumnLayout = {
  size: 44,
  minSize: 40,
  maxSize: 52,
} as const;

export interface DataTableOrganismColumnVisibilityMenuProps<TData extends { id: string }> {
  table: Table<TData>;
  columnVisibilityLabel: string;
}

type OrganismOwnedTableKeys =
  | "columns"
  | "data"
  | "pageSize"
  | "renderToolbar"
  | "pagination"
  | "footerSlot"
  | "globalFilter"
  | "onGlobalFilterChange"
  | "globalSearchKeys"
  | "columnFilters"
  | "onColumnFiltersChange"
  | "fillHeight"
  | "paginationItemLabel"
  | "enableRowSelection"
  | "getRowId"
  | "rowSelection"
  | "onRowSelectionChange";

export interface DataTableOrganismProps<TData extends { id: string }> {
  columns: ColumnDef<TData>[];
  data: TData[];
  pageSize?: number;
  enableSelection?: boolean;
  /**
   * Which rows can be selected when `enableSelection` is on. Defaults to all rows.
   * Pass a predicate on `row.original` to disable/hide checkboxes for ineligible rows.
   */
  enableRowSelection?: boolean | ((row: TData) => boolean);
  /**
   * Controlled selected row ids (`TData.id`). Pair with `onSelectedIdsChange`.
   * Omit for internal/uncontrolled selection (existing consumers).
   */
  selectedIds?: string[];
  /** Notifies when selection ids change (required for controlled `selectedIds`). */
  onSelectedIdsChange?: (ids: string[]) => void;
  /** Disables injected selection checkboxes without hiding eligible-row checkboxes. */
  selectionDisabled?: boolean;
  /**
   * Renders first in the toolbar control cluster (e.g. server-side SearchInput when `enableSearch` is false).
   */
  prependToolbarControls?: ReactNode;
  /** Toolbar search UI; filtering uses global substring match over `searchKeys`. */
  enableSearch?: boolean;
  /**
   * Fields on `row.original` to match (case-insensitive substring).
   * One key limits search to that field; multiple keys search across all listed fields.
   * @default ["email"]
   */
  searchKeys?: (keyof TData & string)[];
  searchPlaceholder?: string;
  enableColumnVisibility?: boolean;
  columnVisibilityLabel?: string;
  /**
   * Pass-through for the table molecule. Search/filter wiring is owned by the organism when
   * `enableSearch` is on — do not pass `globalFilter` / `onGlobalFilterChange` / `globalSearchKeys` here.
   */
  dataTableProps?: Omit<DataTableProps<TData>, OrganismOwnedTableKeys>;
  /**
   * Controlled per-column filter state (TanStack `columnFilters`), forwarded to the molecule.
   * Pair with `onColumnFiltersChange` to share state with external filter UI (e.g. an
   * applied-filters bar or per-column header popovers). Omit for internal/uncontrolled state.
   */
  columnFilters?: ColumnFiltersState;
  /** Notifies when the table would change column filters (required for controlled usage). */
  onColumnFiltersChange?: (next: ColumnFiltersState) => void;
  /**
   * Fill-height mode: table frame stretches to its parent, header stays sticky, body scrolls,
   * pagination stays pinned. Pair with a height-constrained flex parent. Default false.
   */
  fillHeight?: boolean;
  /** Leading region (e.g. title) beside toolbar controls. */
  toolbarTitle?: ReactNode;
  toolbarEndSlot?: ReactNode;
  /**
   * Replaces the default TanStack pagination footer (e.g. server-side page controls).
   * When omitted, the molecule renders the standard page-size pagination.
   */
  paginationFooterSlot?: (table: Table<TData>) => ReactNode;
  /** Plural noun for pagination summary (e.g. `rows`, `profiles`). */
  paginationItemLabel?: string;
}
